import React, { useState, useEffect, useReducer, useRef, forwardRef } from "react";
import styled from "styled-components";
import { valuesReducer } from "../../reducers/valuesReducer";
import classnames from "classnames";
import { dropdownReducer } from "../../reducers/dropdownReducer";
import FormInput from "../Form/FormInput";
import FormDropdown from "../Form/FormDropdown";
import { getRates } from "../../services/BlackboxService";
import { sendCalculatorsQuote } from "./services";
import { REDUCERS } from "../../reducers/reducerConstants";
import { Button } from "react-bootstrap";
import Emailer from "../Emailer";
import { ELOQUA } from "../../utilities/enums";
import IStyledProps from "../../utilities/IStyledProps";
import IEditableProps from "../../utilities/IEditableProps";
import useLanguage from '../../utilities/useLanguage';
import { getTranslation } from './translations';
import { AutomationTestIdFeature, createDataTestId, AutomationTestIdElementType } from '../../utilities/automation';
import { formatValue } from '../../utilities/formatValue';

//-- SUB COMPONENTS -- //
function Results({ resultHeader, loanAmount, CTAText, CTAHyperLink }) {
    const { lang } = useLanguage();
    return (
        <div className="px-2 py-1">
            <h5 className="calc-monthly-payment">{resultHeader || getTranslation(lang).yourMonthlyPayment}</h5>
            <div className="calc-results p-0">
                <strong>
                    <span data-test-id={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.TEXT, specifier: 'monthly_payment'})}>
                        {formatValue({valueStr: loanAmount.main, precision: 2, formatType: '$'})}
                    </span>
                </strong>
            </div>
            {(CTAText && CTAHyperLink) ?
                <Button className="btn inverse small py-2 my-2" data-test-id={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.BUTTON, specifier: 'submit' }) } href={CTAHyperLink}>{CTAText}</Button>
            : null}
            
        </div>
    );
}

function AmountPayment({ amount, currencyClass, label, dataTestId }) {
    return (
        <div className="amount-container mt-3">
            <label>{label}</label>
            <div>
                <strong>
                    <span className={currencyClass} data-test-id={dataTestId}>
                        {formatValue({valueStr: amount, precision: 2, formatType: '$'})}
                    </span>
                </strong>
            </div>
        </div>
    );
}

function findRateById(rates, id) {
    return rates.find(rate => {
        return rate.id === parseInt(id);
    });
}

function parseMoreLess(factor, amount) {
    return parseFloat((amount * factor).toString()).toFixed(2);
}

function getEmailDetails(rates, email, values, loanAmount) {
    const rate = findRateById(rates, values.Rate.value);
    const emailDetails = new FormData();

    emailDetails.append("FirstName", email.FirstName.value);
    emailDetails.append("LastName", email.LastName.value);
    emailDetails.append("EmailAddress", email.Email.value);
    emailDetails.append("Loan1Text", values.LoanPrincipal.value);
    emailDetails.append("Rate1Text", rate.rate.initialRate);
    emailDetails.append("Term1Text", rate.rate.disclosureInformation.termMonths);
    emailDetails.append("Payment1Text", loanAmount.main);
    emailDetails.append("Loan2Text", loanAmount.less.amount);
    emailDetails.append("Payment2Text", loanAmount.less.payment);
    emailDetails.append("Loan3Text", loanAmount.more.amount);
    emailDetails.append("Payment3Text", loanAmount.more.payment);
    emailDetails.append("elqFormName", ELOQUA.AUTO_LOAN_FORM);
    emailDetails.append("elqSiteID", ELOQUA.SITE_ID);
    return emailDetails;
}

function calculateLoanAmount(rates, values) {
    const rate = findRateById(rates, values.Rate.value);
    const monthlyInterest = Number(rate.rate.initialRate) / 100 / 12;
    const term = rate.rate.disclosureInformation.termMonths;
    const amount = Number((values.LoanPrincipal.value || '0'));
    const x = Math.pow(1 + monthlyInterest, term);
    const monthly = (amount * x * monthlyInterest) / (x - 1);

    //create objects and currency string
    const main = parseMoreLess(1, monthly);
    const more = { amount: parseMoreLess(1.20, amount), payment: parseMoreLess(1.20, monthly) };
    const less = { amount: parseMoreLess(.80, amount), payment: parseMoreLess(.80, monthly) };
    return { main, more, less };
}

const EmailContainer = styled.div`
    input {
        border: none;
    }
`;

const CalcContainer = styled.div`
    .arrow-container {
        background-color: #E4E5E6;

        a {
            color: #333;
            text-decoration: none;
            font-size: 1.125rem;
            cursor: pointer;
        }

        a:hover {
            text-decoration: underline;
        }
    }

    .calc-header {
        font-size: 1.75rem;
    }

    .calc-subheader {
        font-size: 1.125rem;
    }

    .calc-left {
        background-color: ${props => props.theme.gray150};
        padding-top: 1.125rem;

        select {
            background-color: #e5e5e5;
            color: #58595d;
            font-size: 1rem;
            line-height: normal;
        }

        select:focus {
            box-shadow: 0 0 0 0.2rem rgba(18, 169, 184, 0);
            outline: -webkit-focus-ring-color auto 1px;
            outline-color: -webkit-focus-ring-color;
            outline-style: auto;
        }

        /* Chrome, Safari, Edge, Opera */
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        /* Firefox */
        input[type=number] {
            -moz-appearance: textfield;
        }
    }

    .calc-right {
        background-color: #E4E5E6;
        padding: 0.8334rem;

        .borrow {
            color: ${props => props.theme.gray625};
            font-size: 1rem;
            font-weight: 700;
        }

        .loantitle {
            color: #000000;
            font-size: 0.875rem;
            margin-bottom: 0px;
        }

        .loanborrow {
            color: #000000;
            font-size: 1rem;
            font-family: "National2";
            font-weight: 700;
        }
    }

    .borrow-more {
        background-color: #DCDCDC;
    }

    .amount-container,
    .payment-container {

        label {
            color: #000000;
            font-size: 0.875rem;
            margin-bottom: 0px;
        }

        .borrow {
            color: #000000;
            font-size: 1rem;
        }

        .payment {
            color: ${props => props.theme.primary};
            font-size: 1rem;
            font-weight: 700;
        }
    }

    .calc-results {
        color: ${props => props.theme.primary};
        font-size: 2rem;
    }

    .calc-monthly-payment {
        font-size: 1.111rem;
        font-family: "National2";
        font-weight: 700;
    }

    .label {
        color: #000000;
        font-size: 0.778rem;
        font-family: "National2";
        font-weight: 300;
        text-align: center;
    }

    .rate-select {
        background-color: #fff !important;
    }

    @media(max-width: 700px) {
        width: 100%;
        max-width: unset;
    }
`;

export interface ICalcProps extends IStyledProps, IEditableProps {
    rateIds: string;
    header: string;
    subHeader?: string;
    children: string | React.ReactChildren;
    loanAmountInput: string;
    CTAText: string;
    CTAHyperLink: string
}

const getFormElements = (loanAmountInput = '25000') => ({
    LoanPrincipal: { value: loanAmountInput, isValid: true, required: true },
    Rate: { value: "3.23", isValid: true, required: true },
})

const emailFormValues = {
    FirstName: { value: "", isValid: false, required: true },
    LastName: { value: "", isValid: false, required: true },
    Email: { value: "", isValid: false, required: true },
};

const Calculator = forwardRef(({
    rateIds,
    header,
    subHeader,
    children: resultHeader,
    cmsId,
    editorClassObj,
    loanAmountInput,
    CTAText,
    CTAHyperLink
}: ICalcProps, ref: any) => {
    const { lang } = useLanguage();
    const rateIdArray = JSON.parse(rateIds);
    const ratesLoaded = useRef(false);
    const [loanAmount, setLoanAmount] = useState(null);
    const [values, valuesDispatch] = useReducer(valuesReducer, { ...getFormElements(loanAmountInput), Rate: { value: rateIdArray[0], isValid: true, required: true } });
    const [rates, setRates] = useState(null);
    const [emailValues, emailValuesDispatch] = useReducer(valuesReducer, emailFormValues);
    const [emailSent, setEmailSent] = useState(false);
    const [showEmailer, setShowEmailer] = useState(false);
    const [dropdownState, dropdownDispatch] = useReducer(dropdownReducer, {
        "Rate": { id: "Rate", name: "Rate" }
    });

    useEffect(() => {
        getLoanRates();
    }, []);

    useEffect(() => {
        if (ratesLoaded.current) {
            doMath();
        }
    }, [values, rates]);

    useEffect(() => {
        if (!isNaN(Number(loanAmountInput))) {
            valuesDispatch({ type: REDUCERS.SET_VALUE, name: 'LoanPrincipal', value: loanAmountInput });
        }
    }, [loanAmountInput])

    function onEmailFormChange(name, value, isValid) {
        emailValuesDispatch({ type: REDUCERS.SET_VALUE, name, value, isValid });
    }

    function loadRateDropdown(rates) {
        const options = rates.map(rate => {
            return { id: rate.id, name: `${formatValue({valueStr: rate.rate.initialRate, formatType: '%', precision: 2 })} - ${rate.rate.disclosureInformation.termMonths} ${getTranslation(lang).months}` };
        });

        dropdownDispatch({ type: REDUCERS.SET_DROPDOWN_ITEMS, id: "Rate", value: options });
    }

    async function getLoanRates() {
        const ratesArray = await getRates(rateIdArray);
        if (ratesArray.length) {
            loadRateDropdown(ratesArray);
            setRates(ratesArray);
            ratesLoaded.current = true;
        }
    }

    function onChange(value: string, name: string) {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: name, value: value || 0 });
    }

    function onDropdownChange(event: any) {
        valuesDispatch({ type: REDUCERS.SET_VALUE, name: event.target.name, value: Number((event.target.value).replace(/[^0-9.-]+/g,"")) });
    }

    function doMath() {
        const loanAmounts = calculateLoanAmount(rates, values);
        setLoanAmount(loanAmounts);
    }

    async function sendEmail(email) {
        const emailDetails = getEmailDetails(rates, email, values, loanAmount);
        const sent = await sendCalculatorsQuote(emailDetails);
        if (sent.toString().match("https://s2213.t.eloqua.com/e/DefaultFormSubmitConfirmation.aspx")) {
            setEmailSent(true);
        }
    }

    //-- CONSTANTS --
    const inputProps = {
        onChange,
        required: true,
        className: "text-left px-3",
        containerClassName: "w-100 mb-4",
        inputClassName: "border-0"
    };

    const dropdownProps = {
        require: true,
        className: "px-3",
        selectClassName: "p-2 border-0 rate-select",
        id: "Rate",
        placeholder: dropdownState["Rate"].name,
        options: dropdownState["Rate"].data,
        onChange: onDropdownChange,
        defaultValue: values["Rate"].value || null
    };

    return (
        <CalcContainer data-id={cmsId} className={editorClassObj} ref={ref}>
            <h4 className="calc-header, pb-2">{header}</h4>
            <p className="calc-subheader"><strong>{subHeader}</strong></p>
            <div className={classnames("w-100", "p-0", "text-center")}>
                <div className="row">
                    <div className="col-8 calc-left">
                        <label className="label font-weight-bold" htmlFor="LoanPrincipal">{getTranslation(lang).loanAmount}</label>
                        <FormInput {...inputProps} id="LoanPrincipal" data-test-id={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.INPUT, specifier: 'loan_amount'})} type="text" value={values.LoanPrincipal.value} intlConfig={{ locale: 'en-US', currency: 'USD' }}/>
                        {rates && (
                            <>
                                <label className="label font-weight-bold" htmlFor="Rate">{getTranslation(lang).aprAndTerm}</label>
                                <FormDropdown {...dropdownProps} dataTestId={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.SELECT, specifier: 'rate'})}/>
                            </>
                        )}
                    </div>
                    <div className="col-4 calc-right">
                        {loanAmount && <>
                            <div className="borrow my-3"><strong>{getTranslation(lang).ifBorrowLess}</strong></div>
                            <AmountPayment
                                dataTestId={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.TEXT, specifier: `borrow_less_loan_amount`})}
                                amount={loanAmount.less.amount}
                                currencyClass="borrow"
                                label={getTranslation(lang).loanAmount} />
                            <AmountPayment
                                dataTestId={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.TEXT, specifier: `borrow_less_payment`})}
                                amount={loanAmount.less.payment}
                                currencyClass="payment"
                                label={getTranslation(lang).monthlyPayment} />
                        </>}
                    </div>
                </div>

                <div className="row">
                    <div className="col-8 calc-left">
                        {loanAmount && <Results resultHeader={resultHeader} loanAmount={loanAmount} CTAText={CTAText} CTAHyperLink={CTAHyperLink} />}
                    </div>
                    <div className="col-4 calc-right borrow-more">
                        {loanAmount && <>
                            <div className="borrow my-3"><strong>{getTranslation(lang).ifBorrowMore}</strong></div>
                            <AmountPayment
                                dataTestId={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.TEXT, specifier: `borrow_more_loan_amount`})}
                                amount={loanAmount.more.amount}
                                currencyClass="borrow"
                                label={getTranslation(lang).loanAmount} />
                            <AmountPayment
                                dataTestId={createDataTestId({feature: AutomationTestIdFeature.PAYMENT_CALCULATOR, element: AutomationTestIdElementType.TEXT, specifier: `borrow_more_payment`})}
                                amount={loanAmount.more.payment}
                                currencyClass="payment"
                                label={getTranslation(lang).monthlyPayment} />
                        </>}
                    </div>
                </div>
                <div className="col-12 p-5 text-center arrow-container d-none">{/* temporarily hide until site forms are decided */}
                    <a onClick={() => setShowEmailer(true)} className="arrow mr-4" >Email me these results</a>
                </div>
            </div>

            {showEmailer && <EmailContainer>
                <Emailer emailValues={emailValues}
                    sendEmail={sendEmail}
                    className="text-dark my-4 px-0"
                    show={true}
                    inputClassName="px-0"
                    emailInputClassName="px-0"
                    emailSent={emailSent}
                    headerClassName="h2 px-0"
                    header="Please provide your info below"
                    onChange={onEmailFormChange} />
            </EmailContainer>}
        </CalcContainer>
    );
});

export default Calculator;
(global as any).Calculator = Calculator;